import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthModal from "./AuthModal";
import BalanceDisplay from "./BalanceDisplay";

import { ADMIN_EMAILS } from "../lib/adminEmails";

type AuthTab = "login" | "signup";

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [authModal, setAuthModal] = useState<AuthTab | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <nav 
        className="sticky top-0 z-50 bg-blue-600"
        style={{ touchAction: 'pan-x' }}
        ref={(el) => {
          if (el) {
            document.documentElement.style.setProperty(
              '--navbar-height',
              `${el.getBoundingClientRect().height}px`
            );
          }
        }}
      >
        <div className="flex items-center">
          {/* Logo - sticky on left, doesn't scroll */}
          <button
            onClick={() => navigate("/", { replace: location.pathname === "/" })}
            className="min-h-[40px] px-3 py-1.5 sm:px-4 sm:py-2 flex items-center gap-2 text-base font-semibold text-white sm:text-lg shrink-0 bg-blue-600"
          >
            <img src="/logo-48.png" alt="EchoNour" className="h-9 w-9 sm:h-10 sm:w-10" />
            <span>EchoNour</span>
          </button>
          
          {/* Scrollable nav items */}
          <div className="flex-1 overflow-x-auto scrollbar-hide" style={{ touchAction: 'pan-x' }}>
            <div className="flex min-w-max items-center justify-between gap-2 pr-3 sm:pr-4 py-1.5 sm:py-2">
              <div className="flex items-center gap-1 sm:gap-4">
                {/* Browse - first after logo */}
                <Link
                  to="/browse"
                  className="min-h-[40px] flex items-center px-2 py-2 text-xs font-medium text-white/70 hover:text-white sm:text-sm"
                >
                  Browse
                </Link>
                {/* Library - available to all (guests see temporary library) */}
                <Link
                  to="/library"
                  className="min-h-[40px] flex items-center px-2 py-2 text-xs font-medium text-white/70 hover:text-white sm:text-sm"
                >
                  Library
                </Link>
                {/* Chat - visible to all, guests redirected to signup */}
                <Link
                  to={user ? "/chat" : "/signup"}
                  className="min-h-[40px] flex items-center px-2 py-2 text-xs font-medium text-white/70 hover:text-white sm:text-sm"
                >
                  Chat<sup className="ml-0.5 text-[8px] sm:text-[9px] font-semibold text-white">beta</sup>
                </Link>
                {/* Help Center - visible to all */}
                <Link
                  to="/help"
                  className="min-h-[40px] flex items-center px-2 py-2 text-xs font-medium text-white/70 hover:text-white sm:text-sm"
                >
                  Help
                </Link>
              </div>

              <div className="flex items-center gap-1 sm:gap-3">
          {user ? (
            <>
              {/* Admin - right side, before balance */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="min-h-[40px] flex items-center px-2 py-2 text-xs font-medium text-white/70 hover:text-white sm:text-sm"
                >
                  Admin
                </Link>
              )}
              <Link
                to="/account"
                className="hidden sm:flex min-h-[40px] items-center px-2 py-2 text-sm text-white/80 hover:text-white"
              >
                {user.name}
              </Link>
              <BalanceDisplay />
              <Link
                to="/pricing"
                className="min-h-[40px] flex items-center px-2 py-2 text-xs font-medium text-white/70 hover:text-white sm:text-sm"
              >
                Pricing
              </Link>
              {/* Feedback button */}
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLScqrCEG1vRsZOVuwkwf6RdN1twJQTXIghF7hLbT5PYrPwv_uA/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-2 py-0.5 rounded-full bg-white text-blue-600 text-xs sm:text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Feedback
              </a>
              {/* Settings dropdown */}
              <div className="relative" ref={settingsRef}>
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className="min-h-[40px] flex items-center px-2 py-2 text-white/70 hover:text-white"
                  title="Settings"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                    />
                  </svg>
                </button>
                
                {settingsOpen && (
                  <div className="fixed right-2 top-12 w-48 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-1 shadow-lg z-[100] text-left">
                    <Link
                      to="/settings#api-keys"
                      onClick={() => setSettingsOpen(false)}
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      🔑 API Keys
                    </Link>
                    <Link
                      to="/settings#preferences"
                      onClick={() => setSettingsOpen(false)}
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      ⚙️ Preferences
                    </Link>
                    <Link
                      to="/settings#speakers"
                      onClick={() => setSettingsOpen(false)}
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      🎭 Persona
                    </Link>
                    <Link
                      to="/settings#privacy"
                      onClick={() => setSettingsOpen(false)}
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      🔒 Privacy
                    </Link>
                    <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                    <button
                      onClick={() => {
                        setSettingsOpen(false);
                        logout();
                        navigate("/");
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/pricing"
                className="min-h-[40px] flex items-center px-2 py-2 text-xs font-medium text-white/70 hover:text-white sm:text-sm"
              >
                Pricing
              </Link>
              <button
                onClick={() => setAuthModal("login")}
                className="min-h-[40px] rounded-md bg-white px-3 py-2 text-xs font-medium text-blue-600 hover:bg-white/90 sm:px-4 sm:text-sm"
              >
                Log in
              </button>
            </>
          )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {authModal && (
        <AuthModal
          initialTab={authModal}
          onClose={() => setAuthModal(null)}
        />
      )}
    </>
  );
}
